export interface ARRejectionConfig {
  missingInformation: string[];
  invalidSubmission: string[];
}

// Per-reward rejection reasons, keyed by the live submission `name`
// (BonusBehaviorSubmissionItem.name, which resolves to string_resource.en).
// Each entry lists the agreed Missing Information / Invalid Submission reasons.
export const AR_REJECTION_REASONS: Record<string, ARRejectionConfig> = {
  "HbA1c Test": {
    missingInformation: [
      "Patient Name",
      "Provider Name/Site",
      "Date of Service",
      "HbA1C Result Value",
      "Test Name or Description",
    ],
    invalidSubmission: [
      "HbA1C Result Not Legible",
      "Invalid Document Type",
      "Submission Does Not Show HbA1C Test",
      "Submission Belongs to Another Individual",
      "Does Not Meet Program Timeframe Requirements",
    ],
  },
  "Flu Shot": {
    missingInformation: [
      "Patient Name",
      "Provider Name/Site",
      "Date of Service",
      "Vaccine Name or Description",
    ],
    invalidSubmission: [
      "Invalid Document Type",
      "Flu Shot Documentation Not Shown",
      "Submission Belongs to Another Individual",
    ],
  },
  "Diabetic Eye Exam": {
    missingInformation: [
      "Patient Name",
      "Provider Name/Site",
      "Date of Service",
      "Eye Exam Name or Description",
    ],
    invalidSubmission: [
      "Invalid Document Type",
      "Diabetic Eye Exam Not Shown",
      "Submission Belongs to Another Individual",
    ],
  },
  "Kidney Health Evaluation": {
    missingInformation: [
      "Patient Name",
      "Provider Name/Site",
      "Date of Service",
      "eGFR Test Documentation",
      "uACR Test Documentation",
    ],
    invalidSubmission: [
      "Invalid Document Type",
      "Kidney Health Evaluation Not Shown",
      "Submission Belongs to Another Individual",
    ],
  },
  "Colon Cancer Screening": {
    missingInformation: [
      "Patient Name",
      "Provider Name/Site",
      "Date of Service",
      "Screening Name or Description",
    ],
    invalidSubmission: [
      "Invalid Document Type",
      "Colon Cancer Screening Not Shown",
      "Submission Belongs to Another Individual",
    ],
  },
  "Cervical Cancer Screening": {
    missingInformation: [
      "Patient Name",
      "Provider Name/Site",
      "Date of Service",
      "Screening Name or Description",
    ],
    invalidSubmission: [
      "Invalid Document Type",
      "Cervical Cancer Screening Not Shown",
      "Submission Belongs to Another Individual",
    ],
  },
  "Breast Cancer Screening": {
    missingInformation: [
      "Patient Name",
      "Provider Name/Site",
      "Date of Service",
      "Screening Name or Description",
    ],
    invalidSubmission: [
      "Invalid Document Type",
      "Breast Cancer Screening Not Shown",
      "Submission Belongs to Another Individual",
    ],
  },
  "Blood Pressure Control": {
    missingInformation: [
      "Patient Name",
      "Provider Name/Site",
      "Date of Service",
      "Blood Pressure Reading",
      "Visit Documentation",
    ],
    invalidSubmission: [
      "Reading From Urgent Care",
      "Reading From Hospitalization",
      "Reading From Retail Pharmacy/Kiosk",
      "Invalid Document Type",
      "Submission Belongs to Another Individual",
    ],
  },
  "Counselor Check-In": {
    missingInformation: [
      "Patient Name",
      "Counselor Name",
      "Counseling Organization Name",
      "Date of Service",
    ],
    invalidSubmission: [
      "Invalid Document Type",
      "Counseling Visit Not Verified",
      "Submission Belongs to Another Individual",
    ],
  },
  "Harm Reduction Check-In": {
    missingInformation: ["Photo of Harm Reduction Supplies"],
    invalidSubmission: [
      "Harm Reduction Supplies Not Visible",
      "Photo Not Clear or Legible",
      "Submission Belongs to Another Individual",
    ],
  },
  "Medication List from Dr Visit": {
    missingInformation: ["Patient Name", "Visit Date", "Medication List"],
    invalidSubmission: [
      "Medication List Not Legible",
      "Invalid Document Type",
      "Visit Date Not in Current Year",
      "Not an After-Visit Summary",
      "Submission Belongs to Another Individual",
    ],
  },
  "Arkansas Launch Account Job Board": {
    missingInformation: ["Personalized Job Board Screenshot"],
    invalidSubmission: [
      "Job Board Not Visible",
      "Account Creation Not Verified",
      "Screenshot Not Legible",
      "Submission Belongs to Another Individual",
    ],
  },
  "Learn New Skills - Grow your Career": {
    missingInformation: [
      "Certificate of Completion",
      "Completion Date",
      "Training Name",
    ],
    invalidSubmission: [
      "Training Not Completed",
      "Completion Date Not in Current Calendar Year",
      "Certificate Not Legible",
      "Invalid Document Type",
      "Submission Belongs to Another Individual",
    ],
  },
};

// Mirrors the BE enum `ActionRewardVerificationType` on
// `bonus_behavior_config.verification`. Used as the fallback for action rewards
// without per-reward reasons defined above, so they keep the generic checklist
// rather than regressing to the legacy single-select.
export type ARVerificationType =
  | "photo"
  | "hba1c"
  | "vaccine"
  | "basic_test"
  | "bp_test"
  | "kidney_test"
  | "counseling"
  | "provider";

const GENERIC_INVALID_REASONS: string[] = [
  "Photo too blurry to read",
  "Wrong document submitted",
  "Duplicate submission",
  "No longer qualifies for this reward",
];

export const VERIFICATION_FALLBACK: Record<
  ARVerificationType,
  ARRejectionConfig
> = {
  kidney_test: {
    missingInformation: [
      "Full name",
      "Date of appointment",
      "Test name (must include both eGFR and uACR)",
      "Provider or clinic name",
    ],
    invalidSubmission: GENERIC_INVALID_REASONS,
  },
  hba1c: {
    missingInformation: [
      "Full name",
      "Date of appointment",
      "Test name",
      "Provider or clinic name",
      "HbA1c measurement value",
    ],
    invalidSubmission: GENERIC_INVALID_REASONS,
  },
  bp_test: {
    missingInformation: [
      "Full name",
      "Date of appointment",
      "Blood pressure reading",
      "Provider or clinic name",
      "Valid source (not ER/urgent care/drug store)",
    ],
    invalidSubmission: GENERIC_INVALID_REASONS,
  },
  basic_test: {
    missingInformation: [
      "Full name",
      "Date of appointment",
      "Test name",
      "Provider or clinic name",
    ],
    invalidSubmission: GENERIC_INVALID_REASONS,
  },
  vaccine: {
    missingInformation: [
      "Full name",
      "Date of appointment",
      "Vaccine name",
      "Provider or clinic name",
    ],
    invalidSubmission: GENERIC_INVALID_REASONS,
  },
  counseling: {
    missingInformation: [
      "Full name",
      "Date of session",
      "Provider or counselor name",
      "Official letter format",
    ],
    invalidSubmission: GENERIC_INVALID_REASONS,
  },
  provider: {
    missingInformation: [
      "Full name",
      "Date of appointment",
      "Doctor or clinic name",
    ],
    invalidSubmission: GENERIC_INVALID_REASONS,
  },
  photo: {
    missingInformation: [],
    invalidSubmission: GENERIC_INVALID_REASONS,
  },
};

export function isARVerification(
  verification: string | null | undefined,
): verification is ARVerificationType {
  return (
    verification != null &&
    Object.keys(VERIFICATION_FALLBACK).includes(verification)
  );
}

// Resolves a submission to its rejection-reason config: the per-reward config
// for a known submission `name`, else the generic config for its `verification`,
// else null (caller falls back to the legacy single-select widget).
export function getARRejectionConfig(
  name: string | null | undefined,
  verification: string | null | undefined,
): ARRejectionConfig | null {
  const trimmedName = name == null ? "" : name.trim();
  if (Object.prototype.hasOwnProperty.call(AR_REJECTION_REASONS, trimmedName)) {
    return AR_REJECTION_REASONS[trimmedName];
  }
  if (isARVerification(verification)) {
    return VERIFICATION_FALLBACK[verification];
  }
  return null;
}
